import { AfterViewInit, ChangeDetectorRef, Component, inject, Input, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { Subscription } from "rxjs";
import { Column, Helper, UpdatedColumnDetails } from "src/app/core/helpers/helper";
import { ExportModalComponent } from "src/app/shared/modal-components/export-modal/export-modal.component";
import { HistoryService } from "src/app/shared/services/history.service";
import { PaginationService } from "src/app/shared/services/pagination.service";
import { ContextMenuComponent, ContextMenuService } from "@perfectmemory/ngx-contextmenu";
import { AddColumnModalComponent } from "src/app/shared/modal-components/add-column-modal/add-column-modal.component";
import { BookingStatus } from "src/app/core/constants/bookingConstant";
import { FilterDataModalComponent } from "src/app/shared/modal-components/filter-data-modal/filter-data-modal.component";
import { DateTime } from "luxon";
import { ExportHistoryModalComponent } from "../export-history-modal/export-history-modal.component";
import { BookingDetailsModalComponent } from "../booking-details/booking-details.component";
import { CommonService } from "../../services/common.service";
import { BookingService } from "../../services/booking.service";
import { TripReassignModalComponent } from "../trip-reassign-modal/trip-reassign-modal.component";
import { BookingStatusChangeConfirmationModalComponent } from "../booking-status-change-confirmation-modal/booking-status-change-confirmation-modal.component";
import { CountryService } from "../../services/country.service";
import { SocketService } from "../../services/socket.service";
import { BookingStatusChangeConfirmationQuickcommerseModalComponent } from "../../quick-commerce-shared-components/booking-status-change-confirmation-quickcommerse-modal/booking-status-change-confirmation-quickcommerse-modal.component";
import { DeliveryBookingStatusChangeModalComponent } from "../../delivery-shared-components/delivery-booking-status-change-modal/delivery-booking-status-change-modal.component";
import { UserType } from "src/app/core/constants/constants";
import { DriverListModelComponent } from "../driver-list-model/driver-list-model.component";

@Component({
  selector: "app-booking-history",
  templateUrl: "./booking-history.component.html",
  styleUrl: "./booking-history.component.scss",
  standalone: false
})
export class BookingHistoryComponent implements OnInit, OnDestroy, AfterViewInit {
  itemsPerPage: number = 0;
  currentPage: number = 1;
  totalPage: number = 0;
  activeBookingTab: number = 0;
  selectedColumnIndex: number = 0;

  bookingHistoryCountryId: string = "";
  bookingHistoryCityId: string = "";
  bookingCountryTimezone: string = "";

  breadCrumbItems!: Array<{}>;
  addedColumn: Array<any> = [];
  itemOptionsPerPage: Array<any> = [];
  bookingHistoryList: Array<any> = [];
  accessibilityList: Array<any> = [];
  columnCanBeAdded: Array<any> = [];
  appliedFiltersLabels: Array<any> = [];
  bookingHistoryCountryList: Array<any> = [];
  bookingHistoryCityList: Array<any> = [];

  allColumns: Column[] = [
    { key: "uniqueId", label: this.helper.translateService.instant("label-title.id"), isSort: true },
    { key: "bookingType", label: this.helper.translateService.instant("label-title.booking-type"), isSort: true },
    { key: "customerDetail.name", label: this.helper.translateService.instant("label-title.customer"), isSort: true },
    { key: "confirmedDriver.name", label: this.helper.translateService.instant("label-title.driver"), isSort: true },
    { key: "vehicleType.name", label: this.helper.translateService.instant("label-title.vehicle-type"), isSort: true },
    { key: "createdAt", label: this.helper.translateService.instant("label-title.create-time"), isSort: true, isDate: true },
    { key: "bookingInvoice.paymentMode", label: this.helper.translateService.instant("label-title.payment-mode"), isHidden: true, isExtra: true },
    { key: "bookingTags", label: this.helper.translateService.instant("label-title.tags"), isHidden: true, isExtra: true }
  ];

  searchText: any;
  previousSearchedText: any;
  selectedBookingHistoryPriceMode: any;
  sortItem: any;
  sortOrder: any;
  exportType: any;
  bookingDetails: any;
  selectedbookingHistoryCountry: any;
  activeColumnMenu: any = null; // Track the currently active menu
  filterData: any = { page: 1, limit: this.itemsPerPage, export: false };

  showNoRecordsFound: boolean = false;

  historySubscription!: Subscription;
  bookingSubscription!: Subscription;

  toLabel = this.helper.translateService.instant("label-title.to");

  bookingHistoryBookingTypeArray: any;

  selectedBusinessType: any = this.helper.AllowBusinessTypeArray[0].key;
  BusinessTypeArray = this.helper.AllowBusinessTypeArray;

  @Input() bookingHistoryMainType!: string;

  @ViewChild("basicMenu") public basicMenu!: ContextMenuComponent<any>;
  @ViewChild("exportModal", { static: true }) exportModal!: ExportModalComponent;
  @ViewChild("addColumnModal", { static: true }) addColumnModal!: AddColumnModalComponent;
  @ViewChild("exportHistoryModal", { static: true }) exportHistoryModal!: ExportHistoryModalComponent;
  @ViewChild("bookingDetailsModal", { static: true }) bookingDetailsModal!: BookingDetailsModalComponent;
  @ViewChild("filterDataModal", { static: true }) filterDataModal!: FilterDataModalComponent;
  @ViewChild("reassignModal", { static: true }) reassignModal!: TripReassignModalComponent;
  @ViewChild("bookingStatusChangeConfirmationModal", { static: true }) bookingStatusChangeConfirmationModal!: BookingStatusChangeConfirmationModalComponent;
  @ViewChild("bookingStatusChangeConfirmationModalQuickCommerse", { static: true })
  bookingStatusChangeConfirmationModalQuickCommerse!: BookingStatusChangeConfirmationQuickcommerseModalComponent;
  @ViewChild("deliveryBookingStatusChangeModal") deliveryBookingStatusChangeModal!: DeliveryBookingStatusChangeModalComponent;
  @ViewChild("driverListModel", { static: true }) driverListModel!: DriverListModelComponent;

  private paginationService = inject(PaginationService);
  private historyService = inject(HistoryService);
  private commonService = inject(CommonService);
  private bookingService = inject(BookingService);
  private countryService = inject(CountryService);
  private socketService = inject(SocketService);

  constructor(public helper: Helper, private _contextMenuService: ContextMenuService<any>, private cdr: ChangeDetectorRef) {}

  /**
   * Lifecycle hook that is called after the component's view has been fully initialized.
   * Sets up event listeners for window resize and initializes scrollable height calculation.
   */
  ngAfterViewInit(): void {
    setTimeout(() => {
      this.helper.calculateScorallableHeight();
    }, 500);
    window.addEventListener("resize", () => {
      this.helper.calculateScorallableHeight();
    });
  }

  /**
   * Initializes the socket connection for booking updates.
   * Joins the room for the current country and sets up event listeners for booking updates.
   *
   * @param fromCountryChange Indicates if the socket connection is being re-established due to a country change.
   */
  bookingSocket(fromCountryChange: boolean = false): void {
    const parameters: any = {
      event: "JOIN_ROOM",
      data: {
        roomId: this.bookingHistoryCountryId
      }
    };
    this.socketService.sendAndListenEvent(this.helper.SocketEvents.JOIN_ROOM, parameters, (data: any) => {
      if (data.data && !fromCountryChange) {
        this.initBookingMonitorSocket();
      }
    });
  }

  /**
   * Sets up a subscription to listen for booking updates from the socket.
   * Updates the booking status and refreshes the booking history list if necessary.
   */
  initBookingMonitorSocket(): void {
    this.bookingSubscription = this.socketService.listener(this.helper.SocketEvents.BOOKINGS_MONITOR).subscribe((res: any) => {
      if (res.data.bookingId) {
        const booking: any = this.bookingHistoryList.find((b: any) => b._id === res.data.bookingId);
        if (booking) {
          booking.status = res.data?.status;
          if (booking.status === this.helper.BookingStatus.ACCEPTED) {
            this.getBookingHistoryList(this.filterData, false);
          }
        }
        this.manageUpdatedBooking(booking, res.data.status);
      }
    });
  }

  /**
   * Leaves the current booking room in the socket connection.
   */
  leaveBookingRoom(): void {
    const parameters: any = {
      event: "LEAVE_ROOM",
      data: {
        roomId: this.bookingHistoryCountryId
      }
    };
    this.socketService.emit(this.helper.SocketEvents.LEAVE_ROOM, parameters);
  }

  /**
   * Manages the updated booking status and refreshes the booking history list if necessary.
   *
   * @param booking The updated booking object.
   * @param bookingStatus The new status of the booking.
   */
  manageUpdatedBooking(booking: any, bookingStatus: number): void {
    const index = this.bookingHistoryList.findIndex((b: any) => b._id === booking?._id);
    if (index !== -1) {
      this.updateBookingStatus(booking);
    } else {
      if (
        (this.bookingHistoryMainType === this.helper.BookingHistoryMainType.RUNNING || this.bookingHistoryMainType === this.helper.BookingHistoryMainType.SCHEDULED) &&
        (bookingStatus ? bookingStatus < this.helper.BookingStatus.ARRIVED_AT_DESTINATION : true) &&
        this.currentPage === 1
      ) {
        this.getBookingHistoryList(this.filterData, false);
      }
      if (
        (this.bookingHistoryMainType === this.helper.BookingHistoryMainType.COMPLETED && bookingStatus >= this.helper.BookingStatus.ARRIVED_AT_DESTINATION) ||
        (this.bookingHistoryMainType === this.helper.BookingHistoryMainType.CANCELLED && bookingStatus === this.helper.BookingStatus.CANCELLED && this.currentPage === 1)
      ) {
        this.getBookingHistoryList(this.filterData, false);
      }
    }
  }

  /**
   * Updates the booking status and refreshes the booking history list if necessary.
   *
   * @param booking The updated booking object.
   */
  updateBookingStatus(booking: any): void {
    if (this.bookingHistoryMainType === this.helper.BookingHistoryMainType.RUNNING && booking.status >= this.helper.BookingStatus.ARRIVED_AT_DESTINATION) {
      if (this.totalPage === this.currentPage && this.currentPage !== 1 && this.bookingHistoryList.length === 1) {
        this.currentPage = this.currentPage - 1;
        this.filterData.page = this.currentPage;
        this.getBookingHistoryList(this.filterData, false);
      } else {
        this.getBookingHistoryList(this.filterData, false);
      }
    }
    if (this.bookingHistoryMainType === this.helper.BookingHistoryMainType.SCHEDULED && booking.status >= this.helper.BookingStatus.IN_ROUTE) {
      if (this.totalPage === this.currentPage && this.currentPage !== 1 && this.bookingHistoryList.length === 1) {
        this.currentPage = this.currentPage - 1;
        this.filterData.page = this.currentPage;
        this.getBookingHistoryList(this.filterData, false);
      } else {
        this.getBookingHistoryList(this.filterData, false);
      }
    }
  }

  /**
   * Lifecycle hook that is called after the component has been initialized.
   * Sets up initial data and subscriptions for the booking history component.
   */
  async ngOnInit(): Promise<void> {
    await this.getBookingHistoryCountryCityList();
    this.setBookingHistoryBookingTypeArray();
    this.bookingSocket();

    this.setFilterForHistoryType();

    switch (this.bookingHistoryMainType) {
      case this.helper.BookingHistoryMainType.RUNNING:
        this.setExportTypeAndColumns(
          this.helper.ExportType.RUNNING,
          [
            { key: "status", labelKey: "label-title.status", index: 8, isSort: true },
            { key: "bookingInvoice.estimated.total", labelKey: "label-title.price", index: 9, isSort: true, isDecimal: true, isCurrencySign: true }
          ],
          [
            this.createColumnConfig("bookingInvoice.estimated.distance", "label-title.estimated-distance", true, true, { isMeterDistance: true }),
            this.createColumnConfig("bookingInvoice.estimated.time", "label-title.estimated-time", true, true, { isSecondTime: true }),
            this.createColumnConfig("bookingInvoice.estimated.driverProfit", "label-title.estimated-driver-earning", true, true, { isDecimal: true, isCurrencySign: true }),
            this.createColumnConfig("pickupAddress.address", "label-title.pickup-address", true, true, { isWrap: true }),
            this.createColumnConfig("customerNote", "label-title.note", true, true)
          ]
        );
        break;

      case this.helper.BookingHistoryMainType.SCHEDULED:
        this.setExportTypeAndColumns(
          this.helper.ExportType.SCHEDULED,
          [
            { key: "status", labelKey: "label-title.status", index: 9, isSort: true },
            { key: "bookingInvoice.estimated.total", labelKey: "label-title.price", index: 10, isSort: true, isDecimal: true, isCurrencySign: true },
            { key: "bookingTime", labelKey: "label-title.schedule-time", index: 8, isSort: true, isDate: true }
          ],
          [
            this.createColumnConfig("bookingInvoice.estimated.distance", "label-title.distance", true, true, { isMeterDistance: true }),
            this.createColumnConfig("bookingInvoice.estimated.time", "label-title.time", true, true, { isSecondTime: true }),
            this.createColumnConfig("bookingInvoice.estimated.driverProfit", "label-title.driver-earning", true, true, { isDecimal: true, isCurrencySign: true }),
            this.createColumnConfig("customerNote", "label-title.note", true, true)
          ]
        );
        break;

      case this.helper.BookingHistoryMainType.COMPLETED:
        this.setExportTypeAndColumns(
          this.helper.ExportType.COMPLETED,
          [
            { key: "completedAt", labelKey: "label-title.complete-time", index: 8, isSort: true, isDate: true },
            { key: "bookingInvoice.actual.total", labelKey: "label-title.price", index: 9, isSort: true, isDecimal: true, isCurrencySign: true },
            { key: "bookingInvoice.paymentStatus", labelKey: "label-title.payment-status", index: 11, isSort: true }
          ],
          [
            this.createColumnConfig("bookingInvoice.actual.distance", "label-title.distance", true, true, { isMeterDistance: true }),
            this.createColumnConfig("bookingInvoice.actual.time", "label-title.time", true, true, { isSecondTime: true }),
            this.createColumnConfig("bookingInvoice.actual.waitingTime", "label-title.wait-time", true, true, { isSecondTime: true }),
            this.createColumnConfig("bookingInvoice.actual.stopWaitingTime", "label-title.stand-by-time", true, true, { isSecondTime: true }),
            this.createColumnConfig("bookingInvoice.actual.trafficTime", "label-title.traffic-time", true, true, { isSecondTime: true }),
            this.createColumnConfig("bookingInvoice.actual.driverProfit", "label-title.driver-earning", true, true, { isDecimal: true, isCurrencySign: true }),
            this.createColumnConfig("rating.customerRate", "label-title.customer-rate", true, true, { isRate: true }),
            this.createColumnConfig("rating.driverRate", "label-title.driver-rate", true, true, { isRate: true }),
            this.createColumnConfig("pickupAddress.address", "heading-title.pickup-address", true, true, { isWrap: true }),
            this.createColumnConfig("customerNote", "label-title.note", true, true)
          ]
        );
        break;

      case this.helper.BookingHistoryMainType.CANCELLED:
        this.setExportTypeAndColumns(
          this.helper.ExportType.CANCELLED,
          [
            { key: "cancelledBy", labelKey: "label-title.cancelled-by", index: 7, isSort: true },
            { key: "completedAt", labelKey: "label-title.cancel-time", index: 9, isSort: true, isDate: true },
            { key: "bookingInvoice.actual.total", labelKey: "label-title.price", index: 10, isSort: true, isDecimal: true, isCurrencySign: true },
            { key: "bookingInvoice.paymentStatus", labelKey: "label-title.payment-status", index: 12, isSort: true },
            { key: "paymentAttempts.failed", labelKey: "label-title.failed-payment-attempts", index: 13 },
            { key: "isDispatched", labelKey: "label-title.is-dispatched", index: 14 }
          ],
          [
            this.createColumnConfig("pickupAddress.address", "heading-title.pickup-address", true, true, { isWrap: true }),
            this.createColumnConfig("customerNote", "label-title.note", true, true),
            this.createColumnConfig("bookingInvoice.actual.driverProfit", "label-title.driver-earning", true, true, { isDecimal: true, isCurrencySign: true })
          ]
        );
        break;

      default:
        break;
    }

    this.itemOptionsPerPage = this.helper.PerPageList;
    this.itemsPerPage = this.itemOptionsPerPage[0];
    this.paginationService.currentPage.next(1);

    this.historySubscription = this.paginationService._currentPageObservable$.subscribe((data: any) => {
      this.showNoRecordsFound = true;
      if (data) {
        this.currentPage = data;
        this.filterData.limit = this.itemsPerPage;
        this.filterData.page = this.currentPage;
        this.filterData.countryId = this.helper.dispatcherDetails.entity.countryId;

        this.getBookingHistoryList(this.filterData);
      }
    });
  }

  /**
   * Fetches the list of countries and cities for booking history.
   * Sets the initial country and city IDs and timezones.
   */
  getBookingHistoryCountryCityList(): Promise<void> {
    return new Promise((resolve) => {
      const parameters: any = {}; // Define parameters for the request
      this.countryService.getCountryCityList(parameters).subscribe((response: any) => {
        this.bookingHistoryCountryList = [];
        if (response?.countries.length) {
          this.bookingHistoryCountryList = response.countries;
          this.bookingHistoryCityList = response.countries[0].cities;

          this.selectedbookingHistoryCountry = this.bookingHistoryCountryList[0];
          this.bookingHistoryCountryId = this.bookingHistoryCountryList[0]._id;
          this.bookingHistoryCityId = this.bookingHistoryCityList[0]._id;
          this.bookingCountryTimezone = this.bookingHistoryCountryList[0].timezones[0];

          this.filterData.countryId = this.bookingHistoryCountryId;
          this.filterData.cityId = this.bookingHistoryCityId;
        }
        resolve();
      });
    });
  }

  /**
   * Sets the export type and columns for the booking history list.
   *
   * @param exportType The type of export (e.g., RUNNING, SCHEDULED, COMPLETED, CANCELLED).
   * @param mainColumns The main columns to be displayed in the booking history list.
   * @param extraColumns Additional columns to be displayed in the booking history list.
   */
  setExportTypeAndColumns(exportType: any, mainColumns: any, extraColumns: any): void {
    this.exportType = exportType;
    mainColumns.forEach((column: any) => this.allColumns.splice(column.index, 0, this.createColumnConfig(column.key, column.labelKey, false, false, column)));
    this.allColumns.push(...extraColumns);
  }

  /**
   * Creates a column configuration object for the booking history list.
   *
   * @param key The key of the column.
   * @param labelKey The label key for the column.
   * @param isHidden Indicates if the column is hidden.
   * @param isExtra Indicates if the column is an extra column.
   * @param additionalProps Additional properties for the column.
   * @returns The column configuration object.
   */
  createColumnConfig(key: any, labelKey: any, isHidden = false, isExtra = false, additionalProps = {}): any {
    return {
      key,
      label: this.helper.translateService.instant(labelKey),
      isHidden,
      isExtra,
      ...additionalProps
    };
  }

  /**
   * Handles the change event for the booking history country selection.
   * Updates the filter data and refreshes the booking history list.
   */
  onBookingHistoryCountryChange(): void {
    this.filterData.countryId = this.bookingHistoryCountryId;
    this.filterData.cityId = this.bookingHistoryCityId;
    this.selectedbookingHistoryCountry = this.bookingHistoryCountryList.find((country: any) => country._id === this.bookingHistoryCountryId);
    this.bookingCountryTimezone = this.selectedbookingHistoryCountry.timezones[0];
    this.getBookingHistoryList(this.filterData);
    this.leaveBookingRoom();
    this.bookingSocket(true);
  }

  /**
   * Sets the filter data based on the booking history main type.
   */
  setFilterForHistoryType(): void {
    switch (this.bookingHistoryMainType) {
      case this.helper.BookingHistoryMainType.RUNNING:
        this.filterData["pageType"] = this.helper.BookingHistoryPageType.RUNNING;
        break;
      case this.helper.BookingHistoryMainType.SCHEDULED:
        this.filterData["pageType"] = this.helper.BookingHistoryPageType.SCHEDULED;
        break;
      case this.helper.BookingHistoryMainType.COMPLETED:
        this.filterData["status"] = this.helper.BookingStatus.ARRIVED_AT_DESTINATION;
        this.filterData["statusOperator"] = "gte";
        // Inert as a filter — tells the server which page to check the dispatcher role against.
        this.filterData["pageType"] = this.helper.BookingHistoryPageType.COMPLETED;
        break;
      case this.helper.BookingHistoryMainType.CANCELLED:
        this.filterData["status"] = this.helper.BookingStatus.CANCELLED;
        this.filterData["pageType"] = this.helper.BookingHistoryPageType.CANCELLED;
        break;
      default:
        console.warn(`Unexpected booking history main type: ${this.bookingHistoryMainType}`);
        break;
    }
  }

  /**
   * Fetches the booking history list based on the provided filter data.
   *
   * @param filterData The filter data for fetching the booking history list.
   * @param isShowLoader Indicates if a loader should be shown during the request.
   */
  getBookingHistoryList(filterData: any, isShowLoader: boolean = true): void {
    filterData = this.setFilterData(filterData);

    let apiRequest: any;
    if (this.bookingHistoryMainType === this.helper.BookingHistoryMainType.RUNNING || this.bookingHistoryMainType === this.helper.BookingHistoryMainType.SCHEDULED) {
      apiRequest = this.historyService.getRunningScheduleBookingHistoryList({ ...filterData, businessType: this.selectedBusinessType, isShowErrorToast: false, isShowLoader });
    } else {
      apiRequest = this.historyService.getBookingHistoryList({ ...filterData, businessType: this.selectedBusinessType, isShowErrorToast: false, isShowLoader });
    }

    apiRequest.subscribe((response: any) => {
      if (response && filterData.export) {
        this.exportHistoryModal.openModal(this.exportType);
        this.filterData.export = false;
        return;
      }
      if (response?.data?.bookings?.length > 0) {
        this.bookingHistoryList = response.data.bookings;

        this.totalPage = Math.ceil(response.data.dataCount / this.itemsPerPage);

        this.paginationService.startIndex = (this.currentPage - 1) * this.itemsPerPage + 1;
        this.paginationService.endIndex = (this.currentPage - 1) * this.itemsPerPage + this.itemsPerPage;
        if (this.paginationService.endIndex > response.data.dataCount) {
          this.paginationService.endIndex = response.data.dataCount;
        }
        this.paginationService.dataLength = response.data.dataCount;
      } else {
        this.bookingHistoryList = [];
        this.totalPage = 0;
        this.paginationService.startIndex = 0;
        this.paginationService.endIndex = 0;
        this.paginationService.dataLength = 0;
      }
    });
  }

  /**
   * Sets the filter data for fetching the booking history list.
   *
   * @param filterData The filter data to be set.
   * @returns The updated filter data.
   */
  setFilterData(filterData: any): any {
    if (!filterData.startDate && !filterData.endDate && this.bookingHistoryMainType !== this.helper.BookingHistoryMainType.RUNNING) {
      let dateRange;
      if (this.bookingHistoryMainType === this.helper.BookingHistoryMainType.SCHEDULED) {
        dateRange = this.helper.getDaysStartEndDate("next", 6, this.bookingCountryTimezone);
      } else {
        dateRange = this.helper.getDaysStartEndDate("previous", 6, this.bookingCountryTimezone);
      }
      const startDate = dateRange.startDate;
      const endDate = dateRange.endDate;
      filterData.formattedDate = `${DateTime.fromJSDate(startDate).toFormat("dd-MM-yyyy")} ${this.toLabel} ${DateTime.fromJSDate(endDate).toFormat("dd-MM-yyyy")}`;
      filterData.startDate = DateTime.fromJSDate(startDate).toFormat("MM-dd-yyyy");
      filterData.endDate = DateTime.fromJSDate(endDate).toFormat("MM-dd-yyyy");

      this.helper.setDefaultDateFilterLabel(this.appliedFiltersLabels, filterData);
    }
    this.filterData = filterData;

    // if (this.bookingHistoryMainType === this.helper.BookingHistoryMainType.HISTORY && Object.keys(this.queryParams).length !== 0) {
    //   delete filterData.countryId;
    // }

    return filterData;
  }

  /**
   * Returns the status string for a given booking status.
   *
   * @param status The booking status.
   * @returns The status string.
   */
  getStatusString(status: number): string {
    return this.helper.BookingStatusStrings[status as BookingStatus];
  }

  /**
   * Returns the payment mode string for a given payment mode.
   *
   * @param paymentMode The payment mode.
   * @returns The payment mode string.
   */
  getPaymentModeString(paymentMode: number): string {
    return this.helper.PaymentModeStrings[paymentMode];
  }

  /**
   * True only on the CANCELLED list when the row has gateway payment attempts and the newest one
   * did not complete (its status is anything but PAID). Guarding on the main type keeps the other
   * tabs' payment-status column untouched.
   *
   * @param data The booking row.
   * @returns True when the last payment attempt is incomplete.
   */
  isLastPaymentAttemptIncomplete(data: any): boolean {
    return (
      this.bookingHistoryMainType === this.helper.BookingHistoryMainType.CANCELLED &&
      data?.paymentAttempts?.lastStatus != null &&
      data.paymentAttempts.lastStatus !== this.helper.PaymentTransactionStatus.PAID
    );
  }

  /**
   * Returns the payment status to render for a row. On the CANCELLED list, when the newest gateway
   * payment attempt did not complete, that attempt's status replaces the invoice's payment status
   * (which would otherwise show e.g. "Paid"). Every other tab keeps the invoice status as-is.
   *
   * @param data The booking row.
   * @returns The payment transaction status to display.
   */
  getDisplayedPaymentStatus(data: any): number {
    if (this.isLastPaymentAttemptIncomplete(data)) {
      return data.paymentAttempts.lastStatus;
    }
    return data.bookingInvoice?.paymentStatus;
  }

  /**
   * Returns an array of booking tags with their corresponding strings.
   *
   * @param bookingTags The array of booking tags.
   * @returns The array of booking tags with their corresponding strings.
   */
  getBookingTagsString(bookingTags: Array<string>): Array<any> {
    const hasBiddingTag = bookingTags.includes(this.helper.BookingType[this.helper.BookingType.BIDDING]);
    return bookingTags
      .filter((tag) => !(hasBiddingTag && tag === this.helper.BookingType[this.helper.BookingType.FIX_FARE]))
      .map((tag) => {
        const key = this.helper.BookingType[tag as keyof typeof this.helper.BookingType];
        let string: string = "";
        if (key && this.helper.getBookingTagColor(tag)) {
          string = this.helper.translateService.instant(this.helper.BookingTypeString[key]);
          return { tag, string };
        }

        return null;
      })
      .filter((item) => item !== null); // Filter out null values
  }

  /**
   * Handles the search event for the booking history list.
   */
  onSearch(): void {
    this.searchText = this.searchText.trim();
    if (this.searchText !== this.previousSearchedText) {
      this.previousSearchedText = JSON.parse(JSON.stringify(this.searchText));
      this.filterData["searchText"] = this.searchText;
      this.getBookingHistoryList(this.filterData);
    }
  }

  /**
   * Opens the export modal for exporting the booking history list.
   */
  openExportModal(): void {
    const metadata: any = {
      tableHeader: this.allColumns
    };

    if (this.exportModal) {
      this.exportModal.open(metadata, this.exportType);
    }
  }

  /**
   * Checks if there are any extra columns available in the booking history list.
   *
   * @returns True if there are extra columns available, false otherwise.
   */
  isExtraColumnAvailable(): boolean {
    return this.allColumns.some((column) => column.isHidden);
  }

  /**
   * Returns the number of added columns in the booking history list.
   *
   * @returns The number of added columns.
   */
  getAddedColumnsLength(): number {
    return this.allColumns.filter((column) => !column.isHidden).length;
  }

  /**
   * Opens the context menu for the booking history list.
   *
   * @param event The mouse event.
   */
  openContextMenu(event: MouseEvent): void {
    event.preventDefault();
    this.cdr.detectChanges();
  }

  /**
   * Removes a column from the booking history list.
   *
   * @param index The index of the column to be removed.
   */
  removeColumn(index: number): void {
    this.allColumns[index].isHidden = true;
  }

  /**
   * Opens the add column modal for adding columns to the booking history list.
   *
   * @param index The index of the column to be added.
   */
  openAddColumnModal(index: number): void {
    this.selectedColumnIndex = index;
    const addedCoumns = this.allColumns.filter((column) => column.isExtra && !column.isHidden);
    const extraColumns = this.allColumns.filter((column) => column.isExtra);
    this.addColumnModal.openModal(addedCoumns, extraColumns);
  }

  /**
   * Updates the columns in the booking history list based on the pushed column details.
   *
   * @param updatedColumnDetails The updated column details.
   */
  pushedColumnUpdate(updatedColumnDetails: UpdatedColumnDetails): void {
    updatedColumnDetails.columns.forEach((bookingColumn: any) => {
      const index = this.allColumns.findIndex((item) => item.key === bookingColumn.key);
      if (index !== -1) {
        this.allColumns[index].isHidden = bookingColumn.isHidden;

        if (updatedColumnDetails.isAdd) {
          // Remove the item from the array
          const [item] = this.allColumns.splice(index, 1);
          // Insert the item back at the desired position
          this.allColumns.splice(this.selectedColumnIndex, 0, item);
        }
      }
    });
  }

  /**
   * Returns the nested value of a booking object based on the provided key.
   *
   * @param obj The booking object.
   * @param key The key to access the nested value.
   * @returns The nested value.
   */
  getBookingNestedValue(obj: any, key: string): any {
    // Split the key by '.' to get individual properties
    const keys = key.split(".");

    // Reduce the keys to access the nested value
    return keys.reduce((acc, curr) => {
      if (curr === "firstName") {
        return `${acc["firstName"]} ${acc["lastName"]}`;
      }
      if (curr === "phone") {
        return `${acc["countryPhoneCode"]} ${acc["phone"]}`;
      }
      if (acc && acc[curr] !== undefined && (curr === "customerRate" || curr === "driverRate")) {
        return Number(acc[curr]?.toFixed(0));
      }
      if (acc && acc[curr] !== undefined) {
        return acc[curr];
      }
      return undefined;
    }, obj);
  }

  /**
   * Returns the distance string for a given booking data and distance.
   *
   * @param data The booking data.
   * @param distance The distance value.
   * @returns The distance string.
   */
  getDistanceString(data: any, distance: number): string {
    const distanceConversion = data.bookingInvoice.distanceUnit === this.helper.DistanceUnit.KM ? 0.001 : 0.000621371;
    const convertedDistance = distance * distanceConversion;
    const distanceString =
      data.bookingInvoice.distanceUnit === this.helper.DistanceUnit.KM
        ? this.helper.translateService.instant("label-title.km")
        : this.helper.translateService.instant("label-title.mile");

    const decimalPointValue = data.decimalPointValue ?? data.bookingInvoice?.decimalPointValue;

    if (Number(convertedDistance.toFixed(decimalPointValue)) > 0) {
      return `${convertedDistance.toFixed(decimalPointValue)} ${distanceString}`;
    }
    return "-";
  }

  /**
   * Returns the time string for a given booking data and time.
   *
   * @param data The booking data.
   * @param time The time value.
   * @returns The time string.
   */
  getTimeString(data: any, time: number): string {
    const convertedTime = time / 60;
    const decimalPointValue = data.decimalPointValue ?? data.bookingInvoice?.decimalPointValue;

    if (Number(convertedTime.toFixed(decimalPointValue)) > 0) {
      return `${convertedTime.toFixed(decimalPointValue)} ${this.helper.translateService.instant("label-title.min")}`;
    }

    return "-";
  }

  /**
   * Returns the booking type string for a given booking type.
   *
   * @param bookingType The booking type.
   * @returns The booking type string.
   */
  getBookingTypeString(bookingType: number): string {
    return this.helper.PriceModeString[bookingType] ?? "";
  }

  /**
   * Handles the change event for the items per page selection.
   *
   * @param item The selected items per page value.
   */
  onChangeItemsPerPage(item: number): void {
    this.itemsPerPage = item;
    this.filterData.limit = this.itemsPerPage;
    this.currentPage = 1;
    this.filterData.page = 1;
    this.getBookingHistoryList(this.filterData);
  }

  /**
   * Exports the booking history list based on the selected columns.
   *
   * @param data The selected columns for export.
   */
  exportHistoryList(data: any): void {
    const result = data.reduce((acc: any, curr: any) => {
      acc[curr.key] = curr.label;
      return acc;
    }, {});

    this.filterData["selectedField"] = JSON.stringify(result);
    this.filterData["export"] = true;

    this.getBookingHistoryList(this.filterData);
  }

  /**
   * Handles the tab click event for the booking history list.
   *
   * @param key The key of the selected tab.
   */
  onTabClicked(key: any): void {
    const hasBookingTypeKey = this.allColumns.some((column) => column.key === "bookingType");

    if (key === "all") {
      if (!hasBookingTypeKey) {
        this.allColumns.splice(1, 0, { key: "bookingType", label: this.helper.translateService.instant("label-title.booking-type"), isSort: true });
      }
      delete this.filterData["bookingType"];
    } else {
      this.selectedBookingHistoryPriceMode = key;
      this.filterData["bookingType"] = this.selectedBookingHistoryPriceMode;

      const idx: number = this.allColumns.findIndex((column) => column.key === "bookingType");
      if (idx !== -1) {
        this.allColumns.splice(idx, 1);
      }
    }

    this.getBookingHistoryList(this.filterData);

    if (window.outerHeight > 800 && window.innerWidth > 575) {
      setTimeout(() => {
        this.helper.calculateScorallableHeight();
      }, 500);
    }
  }

  /**
   * Handles the sort event for the booking history list.
   *
   * @param item The item to be sorted.
   */
  onSort(item: any): void {
    if (this.bookingHistoryList.length === 0) {
      return;
    }

    if (item === this.sortItem) {
      this.sortOrder = this.sortOrder === -1 ? 1 : -1;
    } else {
      this.sortItem = item;
      this.sortOrder = 1;
    }

    this.bookingHistoryList.sort(this.helper.sortData(this.sortItem, this.sortOrder));
  }

  /**
   * Handles the change event for the business type selection.
   */
  onBusinessTypeChange(): void {
    this.filterData["businessType"] = this.selectedBusinessType;
    this.removeVehicleTypeFilter();
    const allTab: HTMLElement | null = document.getElementById("tab-type-all");
    if (allTab) {
      const aTag = allTab.querySelector("a");
      aTag?.click();
    }
    this.onTabClicked("all");
    this.setBookingHistoryBookingTypeArray();
    if (this.isBusinessTypeQuickCommerce() || this.isBusinessTypeDelivery()) {
      this.setQucikCommerceColumns();
    } else if (this.isBusinessTypeService()) {
      this.setServiceColumns();
    } else {
      this.setTaxiColumns();
    }

    localStorage.setItem("businessType", this.selectedBusinessType);

    this.helper.updateAllColumnsFromLocalStorage(this.allColumns);
  }

  /**
   * Removes the vehicle type filter.
   * Deletes the vehicle type filter key from the filter data and removes the vehicle type filter label from the applied filters labels.
   */
  removeVehicleTypeFilter(): void {
    if (this.filterData?.vehicleTypeId) {
      delete this.filterData.vehicleTypeId;
      this.appliedFiltersLabels = this.appliedFiltersLabels.filter((x) => x.key !== "vehicleTypeId");
    }
  }

  setServiceColumns(): void {
    this.allColumns = [
      { key: "uniqueId", label: this.helper.translateService.instant("label-title.id"), isSort: true },
      { key: "bookingType", label: this.helper.translateService.instant("label-title.booking-type"), isSort: true },
      { key: "customerDetail.name", label: this.helper.translateService.instant("label-title.customer"), isSort: true },
      { key: "confirmedDriver.name", label: this.helper.translateService.instant("label-title.driver"), isSort: true },
      { key: "quickCommerce.merchantDetail.name", label: this.helper.translateService.instant("label-title.merchant"), isSort: true },
      { key: "vehicleType.name", label: this.helper.translateService.instant("label-title.service"), isSort: true },
      { key: "city.name", label: this.helper.translateService.instant("label-title.city"), isSort: true },
      { key: "createdType", label: this.helper.translateService.instant("label-title.created-by"), isSort: true },
      { key: "createdAt", label: this.helper.translateService.instant("label-title.create-time"), isSort: true, isDate: true },
      { key: "bookingInvoice.paymentMode", label: this.helper.translateService.instant("label-title.payment-mode") },
      { key: "bookingTags", label: this.helper.translateService.instant("label-title.tags"), isHidden: true, isExtra: true }
    ];

    if (this.selectedBusinessType === this.helper.BusinessType.QUICK_COMMERCE) {
      this.allColumns.splice(3, 0, { key: "quickCommerce.confirmedPicker.name", label: this.helper.translateService.instant("label-title.picker"), isSort: true });
    }

    switch (this.bookingHistoryMainType) {
      case this.helper.BookingHistoryMainType.RUNNING:
        this.setExportTypeAndColumns(
          this.helper.ExportType.RUNNING,
          [
            { key: "status", labelKey: "label-title.driver-status", index: 8, isSort: true },
            { key: "quickCommerce.status", labelKey: "label-title.order-status", index: 9, isSort: true },
            { key: "bookingInvoice.estimated.total", labelKey: "label-title.price", index: 10, isSort: true, isDecimal: true, isCurrencySign: true }
          ],
          [
            this.createColumnConfig("bookingInvoice.estimated.distance", "label-title.distance", true, true, { isMeterDistance: true }),
            this.createColumnConfig("bookingInvoice.estimated.time", "label-title.time", true, true, { isSecondTime: true }),
            this.createColumnConfig("bookingInvoice.estimated.driverProfit", "label-title.estimated-driver-earning", true, true, { isDecimal: true, isCurrencySign: true }),
            this.createColumnConfig("destinationAddresses[0].address", "label-title.destination-address", true, true, { isWrap: true }),
            this.createColumnConfig("customerNote", "label-title.note", true, true)
          ]
        );
        break;

      case this.helper.BookingHistoryMainType.SCHEDULED:
        this.setExportTypeAndColumns(
          this.helper.ExportType.SCHEDULED,
          [
            { key: "status", labelKey: "label-title.status", index: 9, isSort: true },
            { key: "quickCommerce.status", labelKey: "label-title.order-status", index: 10, isSort: true },
            { key: "bookingInvoice.estimated.total", labelKey: "label-title.price", index: 11, isSort: true, isDecimal: true, isCurrencySign: true },
            { key: "bookingTime", labelKey: "label-title.schedule-time", index: 8, isSort: true, isDate: true }
          ],
          [
            this.createColumnConfig("bookingInvoice.estimated.distance", "label-title.distance", true, true, { isMeterDistance: true }),
            this.createColumnConfig("bookingInvoice.estimated.time", "label-title.time", true, true, { isSecondTime: true }),
            this.createColumnConfig("bookingInvoice.estimated.driverProfit", "label-title.driver-earning", true, true, { isDecimal: true, isCurrencySign: true }),
            this.createColumnConfig("destinationAddresses[0].address", "label-title.destination-address", true, true, { isWrap: true }),
            this.createColumnConfig("customerNote", "label-title.note", true, true)
          ]
        );

        break;

      case this.helper.BookingHistoryMainType.COMPLETED:
        this.setExportTypeAndColumns(
          this.helper.ExportType.COMPLETED,
          [
            { key: "completedAt", labelKey: "label-title.complete-time", index: 8, isSort: true, isDate: true },
            { key: "bookingInvoice.actual.total", labelKey: "label-title.price", index: 9, isSort: true, isDecimal: true, isCurrencySign: true },
            { key: "bookingInvoice.paymentStatus", labelKey: "label-title.payment-status", index: 11, isSort: true }
          ],
          [
            this.createColumnConfig("bookingInvoice.actual.distance", "label-title.distance", true, true, { isMeterDistance: true }),
            this.createColumnConfig("bookingInvoice.actual.time", "label-title.time", true, true, { isSecondTime: true }),
            this.createColumnConfig("bookingInvoice.actual.driverProfit", "label-title.driver-earning", true, true, { isDecimal: true, isCurrencySign: true }),
            this.createColumnConfig("rating.customerRate", "label-title.customer-rate", true, true, { isRate: true }),
            this.createColumnConfig("rating.driverRate", "label-title.driver-rate", true, true, { isRate: true }),
            this.createColumnConfig("destinationAddresses[0].address", "label-title.destination-address", true, true, { isWrap: true }),
            this.createColumnConfig("customerNote", "label-title.note", true, true)
          ]
        );
        break;

      case this.helper.BookingHistoryMainType.CANCELLED:
        this.setExportTypeAndColumns(
          this.helper.ExportType.CANCELLED,
          [
            { key: "cancelledBy", labelKey: "label-title.cancelled-by", index: 7, isSort: true },
            { key: "completedAt", labelKey: "label-title.cancel-time", index: 9, isSort: true, isDate: true },
            { key: "bookingInvoice.actual.total", labelKey: "label-title.price", index: 10, isSort: true, isDecimal: true, isCurrencySign: true },
            { key: "bookingInvoice.paymentStatus", labelKey: "label-title.payment-status", index: 12, isSort: true },
            { key: "paymentAttempts.failed", labelKey: "label-title.failed-payment-attempts", index: 13 },
            { key: "isDispatched", labelKey: "label-title.is-dispatched", index: 14 }
          ],
          [
            this.createColumnConfig("bookingInvoice.actual.distance", "label-title.distance", true, true, { isMeterDistance: true }),
            this.createColumnConfig("destinationAddresses[0].address", "label-title.destination-address", true, true, { isWrap: true }),
            this.createColumnConfig("customerNote", "label-title.note", true, true),
            this.createColumnConfig("bookingInvoice.actual.driverProfit", "label-title.driver-earning", true, true, { isDecimal: true, isCurrencySign: true })
          ]
        );
        break;
      default:
        break;
    }
  }

  /**
   * Sets the columns for the taxi business type.
   */
  setTaxiColumns(): void {
    this.allColumns = [
      { key: "uniqueId", label: this.helper.translateService.instant("label-title.id"), isSort: true },
      { key: "bookingType", label: this.helper.translateService.instant("label-title.booking-type"), isSort: true },
      { key: "customerDetail.name", label: this.helper.translateService.instant("label-title.customer"), isSort: true },
      { key: "confirmedDriver.name", label: this.helper.translateService.instant("label-title.driver"), isSort: true },
      { key: "vehicleType.name", label: this.helper.translateService.instant("label-title.vehicle-type"), isSort: true },
      { key: "city.name", label: this.helper.translateService.instant("label-title.city"), isSort: true },
      { key: "createdType", label: this.helper.translateService.instant("label-title.created-by"), isSort: true },
      { key: "createdAt", label: this.helper.translateService.instant("label-title.create-time"), isSort: true, isDate: true },
      { key: "bookingInvoice.paymentMode", label: this.helper.translateService.instant("label-title.payment-mode") },
      { key: "bookingTags", label: this.helper.translateService.instant("label-title.tags"), isHidden: true, isExtra: true }
    ];

    switch (this.bookingHistoryMainType) {
      case this.helper.BookingHistoryMainType.RUNNING:
        this.setExportTypeAndColumns(
          this.helper.ExportType.RUNNING,
          [
            { key: "status", labelKey: "label-title.status", index: 8, isSort: true },
            { key: "bookingInvoice.estimated.total", labelKey: "label-title.price", index: 9, isSort: true, isDecimal: true, isCurrencySign: true }
          ],
          [
            this.createColumnConfig("bookingInvoice.estimated.distance", "label-title.estimated-distance", true, true, { isMeterDistance: true }),
            this.createColumnConfig("bookingInvoice.estimated.time", "label-title.estimated-time", true, true, { isSecondTime: true }),
            this.createColumnConfig("bookingInvoice.estimated.driverProfit", "label-title.estimated-driver-earning", true, true, { isDecimal: true, isCurrencySign: true }),
            this.createColumnConfig("pickupAddress.address", "label-title.pickup-address", true, true, { isWrap: true }),
            this.createColumnConfig("customerNote", "label-title.note", true, true)
          ]
        );
        break;

      case this.helper.BookingHistoryMainType.SCHEDULED:
        this.setExportTypeAndColumns(
          this.helper.ExportType.SCHEDULED,
          [
            { key: "status", labelKey: "label-title.status", index: 9, isSort: true },
            { key: "bookingInvoice.estimated.total", labelKey: "label-title.price", index: 10, isSort: true, isDecimal: true, isCurrencySign: true },
            { key: "bookingTime", labelKey: "label-title.schedule-time", index: 8, isSort: true, isDate: true }
          ],
          [
            this.createColumnConfig("bookingInvoice.estimated.distance", "label-title.distance", true, true, { isMeterDistance: true }),
            this.createColumnConfig("bookingInvoice.estimated.time", "label-title.time", true, true, { isSecondTime: true }),
            this.createColumnConfig("bookingInvoice.estimated.driverProfit", "label-title.driver-earning", true, true, { isDecimal: true, isCurrencySign: true }),
            this.createColumnConfig("customerNote", "label-title.note", true, true)
          ]
        );
        this.setBookingHistoryBookingTypeArray();
        break;

      case this.helper.BookingHistoryMainType.COMPLETED:
        this.setExportTypeAndColumns(
          this.helper.ExportType.COMPLETED,
          [
            { key: "completedAt", labelKey: "label-title.complete-time", index: 8, isSort: true, isDate: true },
            { key: "bookingInvoice.actual.total", labelKey: "label-title.price", index: 9, isSort: true, isDecimal: true, isCurrencySign: true },
            { key: "bookingInvoice.paymentStatus", labelKey: "label-title.payment-status", index: 11, isSort: true }
          ],
          [
            this.createColumnConfig("bookingInvoice.actual.distance", "label-title.distance", true, true, { isMeterDistance: true }),
            this.createColumnConfig("bookingInvoice.actual.time", "label-title.time", true, true, { isSecondTime: true }),
            this.createColumnConfig("bookingInvoice.actual.waitingTime", "label-title.wait-time", true, true, { isSecondTime: true }),
            this.createColumnConfig("bookingInvoice.actual.stopWaitingTime", "label-title.stand-by-time", true, true, { isSecondTime: true }),
            this.createColumnConfig("bookingInvoice.actual.trafficTime", "label-title.traffic-time", true, true, { isSecondTime: true }),
            this.createColumnConfig("bookingInvoice.actual.driverProfit", "label-title.driver-earning", true, true, { isDecimal: true, isCurrencySign: true }),
            this.createColumnConfig("rating.customerRate", "label-title.customer-rate", true, true, { isRate: true }),
            this.createColumnConfig("rating.driverRate", "label-title.driver-rate", true, true, { isRate: true }),
            this.createColumnConfig("pickupAddress.address", "heading-title.pickup-address", true, true, { isWrap: true }),
            this.createColumnConfig("customerNote", "label-title.note", true, true)
          ]
        );
        break;

      case this.helper.BookingHistoryMainType.CANCELLED:
        this.setExportTypeAndColumns(
          this.helper.ExportType.CANCELLED,
          [
            { key: "cancelledBy", labelKey: "label-title.cancelled-by", index: 7, isSort: true },
            { key: "completedAt", labelKey: "label-title.cancel-time", index: 9, isSort: true, isDate: true },
            { key: "bookingInvoice.actual.total", labelKey: "label-title.price", index: 10, isSort: true, isDecimal: true, isCurrencySign: true },
            { key: "bookingInvoice.paymentStatus", labelKey: "label-title.payment-status", index: 12, isSort: true },
            { key: "paymentAttempts.failed", labelKey: "label-title.failed-payment-attempts", index: 13 },
            { key: "isDispatched", labelKey: "label-title.is-dispatched", index: 14 }
          ],
          [
            this.createColumnConfig("pickupAddress.address", "heading-title.pickup-address", true, true, { isWrap: true }),
            this.createColumnConfig("customerNote", "label-title.note", true, true),
            this.createColumnConfig("bookingInvoice.actual.driverProfit", "label-title.driver-earning", true, true, { isDecimal: true, isCurrencySign: true })
          ]
        );
        break;

      default:
        break;
    }
  }

  setBookingHistoryBookingTypeArray(): void {
    if (this.selectedBusinessType === this.helper.BusinessType.TAXI) {
      this.bookingHistoryBookingTypeArray = this.helper.BookingHistoryBookingTypeArray.filter((x: any) => {
        return x.key === this.helper.PriceMode.NORMAL || x.key === this.helper.PriceMode.RENTAL || x.key === this.helper.PriceMode.SHARE;
      });
    } else if (this.selectedBusinessType === this.helper.BusinessType.QUICK_COMMERCE) {
      this.bookingHistoryBookingTypeArray = this.helper.BookingHistoryBookingTypeArray.filter((x: any) => {
        return x.key === this.helper.PriceMode.QUICK_COMMERCE;
      });
    } else if (this.selectedBusinessType === this.helper.BusinessType.DELIVERY) {
      this.bookingHistoryBookingTypeArray = this.helper.BookingHistoryBookingTypeArray.filter((x: any) => {
        return x.key === this.helper.PriceMode.PICKUP || x.key === this.helper.PriceMode.DELIVERY;
      });
    } else if (this.selectedBusinessType === this.helper.BusinessType.SERVICE) {
      this.bookingHistoryBookingTypeArray = this.helper.BookingHistoryBookingTypeArray.filter((x: any) => {
        return x.key === this.helper.PriceMode.ON_SITE_SERVICE || x.key === this.helper.PriceMode.SERVICE_AT_HOME || x.key === this.helper.PriceMode.ONLINE_SERVICE;
      });
    } else if (this.selectedBusinessType === this.helper.BusinessType.TAXI_METER) {
      this.bookingHistoryBookingTypeArray = this.helper.BookingHistoryBookingTypeArray.filter((x: any) => {
        return x.key === this.helper.PriceMode.TAXI_METER;
      });
    } else if (this.selectedBusinessType === this.helper.BusinessType.TOWING) {
      this.bookingHistoryBookingTypeArray = this.helper.BookingHistoryBookingTypeArray.filter((x: any) => {
        return x.key === this.helper.PriceMode.TOWING;
      });
    }

    if (this.selectedBusinessType === this.helper.BusinessType.TAXI && this.bookingHistoryMainType === this.helper.BookingHistoryMainType.SCHEDULED) {
      this.bookingHistoryBookingTypeArray = this.helper.BookingHistoryBookingTypeArray.filter((x: any) => {
        return x.key === this.helper.PriceMode.NORMAL || x.key === this.helper.PriceMode.RENTAL;
      });
    }
  }

  /**
   * Sets the columns for the quick commerce business type.
   */
  setQucikCommerceColumns(): void {
    this.allColumns = [
      { key: "uniqueId", label: this.helper.translateService.instant("label-title.id"), isSort: true },
      { key: "bookingType", label: this.helper.translateService.instant("label-title.booking-type"), isSort: true },
      { key: "customerDetail.name", label: this.helper.translateService.instant("label-title.customer"), isSort: true },
      { key: "confirmedDriver.name", label: this.helper.translateService.instant("label-title.driver"), isSort: true },
      { key: "quickCommerce.merchantDetail.name", label: this.helper.translateService.instant("label-title.merchant"), isSort: true },
      { key: "quickCommerce.confirmedPicker.name", label: this.helper.translateService.instant("label-title.picker"), isSort: true },
      { key: "city.name", label: this.helper.translateService.instant("label-title.city"), isSort: true },
      { key: "createdType", label: this.helper.translateService.instant("label-title.created-by"), isSort: true },
      { key: "createdAt", label: this.helper.translateService.instant("label-title.create-time"), isSort: true, isDate: true },
      { key: "bookingInvoice.paymentMode", label: this.helper.translateService.instant("label-title.payment-mode") },
      { key: "bookingTags", label: this.helper.translateService.instant("label-title.tags"), isHidden: true, isExtra: true }
    ];

    switch (this.bookingHistoryMainType) {
      case this.helper.BookingHistoryMainType.RUNNING:
        this.setExportTypeAndColumns(
          this.helper.ExportType.RUNNING,
          [
            { key: "status", labelKey: "label-title.status", index: 8, isSort: true },
            { key: "quickCommerce.status", labelKey: "label-title.order-status", index: 9, isSort: true },
            { key: "bookingInvoice.estimated.total", labelKey: "label-title.price", index: 10, isSort: true, isDecimal: true, isCurrencySign: true }
          ],
          [
            // this.createColumnConfig("bookingInvoice.estimated.distance", "label-title.estimated-distance", true, true, { isMeterDistance: true }),
            this.createColumnConfig("bookingInvoice.estimated.time", "label-title.estimated-time", true, true, { isSecondTime: true }),
            this.createColumnConfig("bookingInvoice.estimated.driverProfit", "label-title.estimated-driver-earning", true, true, { isDecimal: true, isCurrencySign: true }),
            this.createColumnConfig("destinationAddresses[0].address", "label-title.destination-address", true, true, { isWrap: true }),
            this.createColumnConfig("customerNote", "label-title.note", true, true)
          ]
        );
        break;

      case this.helper.BookingHistoryMainType.COMPLETED:
        this.setExportTypeAndColumns(
          this.helper.ExportType.COMPLETED,
          [
            { key: "completedAt", labelKey: "label-title.complete-time", index: 8, isSort: true, isDate: true },
            { key: "bookingInvoice.actual.total", labelKey: "label-title.price", index: 9, isSort: true, isDecimal: true, isCurrencySign: true },
            { key: "bookingInvoice.paymentStatus", labelKey: "label-title.payment-status", index: 11, isSort: true }
          ],
          [
            // this.createColumnConfig("bookingInvoice.actual.distance", "label-title.distance", true, true, { isMeterDistance: true }),
            this.createColumnConfig("bookingInvoice.actual.time", "label-title.time", true, true, { isSecondTime: true }),
            this.createColumnConfig("bookingInvoice.actual.waitingTime", "label-title.wait-time", true, true, { isSecondTime: true }),
            // this.createColumnConfig("bookingInvoice.actual.stopWaitingTime", "label-title.stand-by-time", true, true, { isSecondTime: true }),
            this.createColumnConfig("bookingInvoice.actual.trafficTime", "label-title.traffic-time", true, true, { isSecondTime: true }),
            this.createColumnConfig("bookingInvoice.actual.driverProfit", "label-title.driver-earning", true, true, { isDecimal: true, isCurrencySign: true }),
            this.createColumnConfig("rating.customerRate", "label-title.customer-rate", true, true, { isRate: true }),
            this.createColumnConfig("rating.driverRate", "label-title.driver-rate", true, true, { isRate: true }),
            this.createColumnConfig("destinationAddresses[0].address", "label-title.destination-address", true, true, { isWrap: true }),
            this.createColumnConfig("customerNote", "label-title.note", true, true)
          ]
        );
        break;

      case this.helper.BookingHistoryMainType.CANCELLED:
        this.setExportTypeAndColumns(
          this.helper.ExportType.CANCELLED,
          [
            { key: "cancelledBy", labelKey: "label-title.cancelled-by", index: 7, isSort: true },
            { key: "completedAt", labelKey: "label-title.cancel-time", index: 9, isSort: true, isDate: true },
            { key: "bookingInvoice.actual.total", labelKey: "label-title.price", index: 10, isSort: true, isDecimal: true, isCurrencySign: true },
            { key: "bookingInvoice.paymentStatus", labelKey: "label-title.payment-status", index: 12, isSort: true },
            { key: "paymentAttempts.failed", labelKey: "label-title.failed-payment-attempts", index: 13 },
            { key: "isDispatched", labelKey: "label-title.is-dispatched", index: 14 }
          ],
          [
            this.createColumnConfig("destinationAddresses[0].address", "label-title.destination-address", true, true, { isWrap: true }),
            this.createColumnConfig("customerNote", "label-title.note", true, true),
            this.createColumnConfig("bookingInvoice.actual.driverProfit", "label-title.driver-earning", true, true, { isDecimal: true, isCurrencySign: true })
          ]
        );
        break;

      default:
        break;
    }
  }

  /**
   * Checks if the selected business type is taxi.
   *
   * @returns True if the selected business type is taxi, false otherwise.
   */
  isBusinessTypeTaxi(): boolean {
    return this.selectedBusinessType === this.helper.BusinessType.TAXI;
  }

  isBusinessTypeTaxiMeter(): boolean {
    return this.selectedBusinessType === this.helper.BusinessType.TAXI_METER;
  }

  isBusinessTypeTowing(): boolean {
    return this.selectedBusinessType === this.helper.BusinessType.TOWING;
  }

  /**
   * Checks if the selected business type is quick commerce.
   *
   * @returns True if the selected business type is quick commerce, false otherwise.
   */
  isBusinessTypeQuickCommerce(): boolean {
    return this.selectedBusinessType === this.helper.BusinessType.QUICK_COMMERCE;
  }

  /**
   * Checks if the selected business type is delivery.
   *
   * @returns True if the selected business type is delivery, false otherwise.
   */
  isBusinessTypeDelivery(): boolean {
    return this.selectedBusinessType === this.helper.BusinessType.DELIVERY;
  }

  /**
   * Checks if the selected business type is delivery.
   *
   * @returns True if the selected business type is delivery, false otherwise.
   */
  isBusinessTypeService(): boolean {
    return this.selectedBusinessType === this.helper.BusinessType.SERVICE;
  }

  isBookingTypePickup(booking: any): boolean {
    return booking.bookingType === this.helper.PriceMode.PICKUP;
  }

  /**
   * Opens the booking details modal for a given booking ID.
   *
   * @param bookingId The booking ID.
   */
  openBookingDetailsModal(bookingId: string): void {
    this.bookingDetailsModal.openModal(this.bookingHistoryMainType, bookingId);
  }

  /**
   * Opens the filter modal for filtering the booking history list.
   */
  openFilterModal(): void {
    const fieldsToFilter: Array<any> = [this.helper.FilterFields.CITY, this.helper.FilterFields.TYPE, this.helper.FilterFields.BOOKING_TAG, this.helper.FilterFields.DATE];

    if (this.bookingHistoryMainType === this.helper.BookingHistoryMainType.RUNNING) {
      fieldsToFilter.push(this.helper.FilterFields.BOOKING_STATUS);
    }

    // Only pre-acceptance bookings can be paused, so the filter is offered on the running/scheduled views only.
    if (this.bookingHistoryMainType === this.helper.BookingHistoryMainType.RUNNING || this.bookingHistoryMainType === this.helper.BookingHistoryMainType.SCHEDULED) {
      fieldsToFilter.push(this.helper.FilterFields.PAUSED);
    }

    let isScheduleDate: boolean = false;
    if (this.bookingHistoryMainType === this.helper.BookingHistoryMainType.SCHEDULED) {
      isScheduleDate = true;
    }

    const dateLabel = "label-title.created-date";
    this.filterDataModal.openModal(this.helper.FilterType.BOOKING_HISTORY, fieldsToFilter, this.filterData, dateLabel, isScheduleDate);
  }

  /**
   * Handles the filter applied event for the booking history list.
   *
   * @param filteredDetails The filtered details.
   */
  onFilterApplied(filteredDetails: any): void {
    this.filterData = filteredDetails.filteredDetails;
    this.setFilterForHistoryType();
    this.appliedFiltersLabels = filteredDetails.appliedFilterDetailsToShowInParent;

    this.filterData["page"] = 1;
    this.currentPage = 1;

    this.getBookingHistoryList(this.filterData);

    setTimeout(() => {
      this.helper.calculateScorallableHeight();
    }, 500);
  }

  /**
   * Clears the filter for the booking history list.
   *
   * @param data The filter data to be cleared.
   */
  clearFilter(data: any): void {
    this.filterData = data.filterData;
    this.appliedFiltersLabels = data.appliedFiltersLabels;
    if (this.appliedFiltersLabels.length === 0) {
      this.filterData = { page: 1, limit: this.itemsPerPage, export: false };
      this.setFilterForHistoryType();
    }

    if (data.filterData.bookingType) {
      this.filterData.bookingType = data.filterData.bookingType;
    }
    this.getBookingHistoryList(this.filterData);
    setTimeout(() => {
      this.helper.calculateScorallableHeight();
    }, 500);
  }

  /**
   * Clears the search text for the booking history list.
   */
  clearSearch(): void {
    this.searchText = "";
    if (!this.filterData.searchText) return;
    delete this.filterData.searchText;
    this.currentPage = 1;
    this.filterData.page = 1;
    this.getBookingHistoryList(this.filterData);
  }

  /**
   * Lifecycle hook that is called when the component is destroyed.
   * Cleans up subscriptions and leaves the booking room in the socket connection.
   */
  ngOnDestroy(): void {
    if (this.basicMenu) {
      this._contextMenuService.closeAll();
      this.activeColumnMenu = null;
    }

    this.historySubscription?.unsubscribe();
    this.bookingSubscription?.unsubscribe();
    this.leaveBookingRoom();
  }

  /**
   * Opens the reassign modal for reassigning a booking.
   *
   * @param data The booking data.
   */
  onReassignBooking(data: any, bookingType: any): void {
    this.bookingDetails = data;
    this.reassignModal.openModal(this.bookingDetails, bookingType);
  }

  /**
   * Reassigns a booking to a new driver.
   *
   * @param driverId The ID of the new driver.
   */
  reassignBooking(driverId: any): void {
    const json = {
      bookingId: this.bookingDetails._id,
      driverId: driverId
    };

    this.bookingService.reassignBooking(json).subscribe(() => {
      this.getBookingHistoryList(this.filterData);
      if (this.reassignModal) {
        this.reassignModal.closeModal();
      }
    });
  }

  /**
   * Opens the booking status change confirmation modal.
   *
   * @param bookingDetails The booking details.
   * @param isCancelBooking Indicates if the booking is being cancelled.
   */
  openBookingStatusChangeConfirmationModal(bookingDetails: any, isCancelBooking: boolean = false): void {
    this.bookingStatusChangeConfirmationModal.openModal(bookingDetails, "", isCancelBooking, this.selectedBusinessType);
  }

  /**
   * Opens the booking status change confirmation modal for quick commerce.
   *
   * @param bookingDetails The booking details.
   * @param isCancelBooking Indicates if the booking is being cancelled.
   */
  openBookingStatusChangeConfirmationModalForQuickCommerce(bookingDetails: any, isCancelBooking: boolean = false): void {
    this.bookingStatusChangeConfirmationModalQuickCommerse.openModal(bookingDetails, "", isCancelBooking);
  }

  /**
   * Opens the booking status change confirmation modal for delivery bookings.
   *
   * @param bookingDetails The booking details.
   * @param isCancelBooking Indicates if the booking is being cancelled.
   */
  openDeliveryBookingStatusChangeConfirmationModal(bookingDetails: any, isCancelBooking: boolean = false): void {
    this.deliveryBookingStatusChangeModal?.openModal(bookingDetails, "", isCancelBooking);
  }

  openServiceAssignDriverMerchantModal(bookingDetails: any, assignTo: UserType): void {
    this.deliveryBookingStatusChangeModal?.openModal(bookingDetails, this.selectedbookingHistoryCountry, false, assignTo);
  }

  /**
   * Handles the booking status change event.
   *
   * @param bookingDetails The booking details.
   */
  onBookingStatusChange(bookingDetails: any): void {
    if (bookingDetails?.status >= this.helper.BookingStatus.ARRIVED_AT_DESTINATION) {
      this.bookingHistoryList = this.bookingHistoryList.filter((b: any) => b._id !== bookingDetails._id);
    }

    this.getBookingHistoryList(this.filterData);
  }

  /**
   * Checks if the quick commerce status change button should be shown for a given booking status.
   *
   * @param quickCommerceStatus The quick commerce status.
   * @returns True if the quick commerce status change button should be shown, false otherwise.
   */
  isShowQcStatusChangeBtn(quickCommerceStatus: number): boolean {
    return (
      quickCommerceStatus === this.helper.BookingStatus.REQUESTED ||
      quickCommerceStatus === this.helper.BookingStatus.MERCHANT_ACCEPTED ||
      quickCommerceStatus === this.helper.BookingStatus.NO_PICKER_FOUND ||
      quickCommerceStatus === this.helper.BookingStatus.ARRIVED_AT_PICKUP ||
      (quickCommerceStatus >= this.helper.BookingStatus.PICKED && quickCommerceStatus < this.helper.BookingStatus.ARRIVED_AT_DESTINATION)
    );
  }

  /**
   * Handles the quick commerce booking status change event.
   * Updates the quick commerce status in the booking history list.
   *
   * @param bookingDetails The booking details.
   */
  onChangeQcBookingStatus(bookingDetails: any): void {
    if (bookingDetails?.quickCommerce?.status === this.helper.BookingStatus.PICKER_REJECTED) {
      this.getBookingHistoryList(this.filterData);
    } else if (bookingDetails?.quickCommerce?.status === this.helper.BookingStatus.ARRIVED_AT_DESTINATION) {
      this.bookingHistoryList = this.bookingHistoryList.filter((b: any) => b._id !== bookingDetails._id);
      this.getBookingHistoryList(this.filterData);
    } else {
      const booking = this.bookingHistoryList.find((b: any) => b._id === bookingDetails._id);
      if (booking) {
        booking.quickCommerce.status = bookingDetails.quickCommerce?.status;
      }
    }
  }
  getAvailableBookingActions(data: any): any[] {
    const actions: any[] = [];

    const actionGenerators = this.getBookingActionsAsBusinessType(data);

    for (const { condition, action, isArray } of actionGenerators) {
      if (condition) {
        const result = action();
        isArray ? actions.push(...result) : actions.push(result);
      }
    }

    return actions;
  }

  getBookingActionsAsBusinessType(data: any): any[] {
    let actions: any[] = [{ condition: this.shouldShowAcceptRejectAction(data), action: () => this.getAcceptRejectActionChange(data) }];

    if (data.businessType === this.helper.BusinessType.TAXI) {
      actions = [...actions, ...this.getTaxiBookingActions(data)];
    } else if (data.businessType === this.helper.BusinessType.QUICK_COMMERCE) {
      actions = [...actions, ...this.getQuickCommerceBookingActions(data)];
    } else if (data.businessType === this.helper.BusinessType.DELIVERY) {
      actions = [...actions, ...this.getDeliveryBookingActions(data)];
    } else if (data.businessType === this.helper.BusinessType.SERVICE) {
      actions = [...actions, ...this.getServiceBookingActions(data)];
    } else if (data.businessType === this.helper.BusinessType.TAXI_METER) {
      actions = [...actions, ...this.getTaxiMeterBookingActions(data)];
    } else if (data.businessType === this.helper.BusinessType.TOWING) {
      actions = [...actions, ...this.getTowingBookingActions(data)];
    }
    actions.push({ condition: this.shouldShowPauseAction(data), action: () => this.getPauseAction(data) });
    actions.push({ condition: this.shouldShowCancelAction(data), action: () => this.getCancelAction(data) });

    return actions;
  }

  getTaxiBookingActions(data: any): any[] {
    return [
      { condition: this.shouldShowDriverStatusChange(data), action: () => this.getBookingDeliveryActionChange(data) },
      { condition: this.isReAssignBooking(data), action: () => this.getReassignBookingAction(data) }
    ];
  }

  getTaxiMeterBookingActions(data: any): any[] {
    return [
      { condition: this.shouldShowDriverStatusChange(data), action: () => this.getBookingDeliveryActionChange(data) },
      { condition: this.isReAssignBooking(data), action: () => this.getReassignBookingAction(data) }
    ];
  }

  getTowingBookingActions(data: any): any[] {
    return [
      { condition: this.shouldShowDriverStatusChange(data), action: () => this.getBookingDeliveryActionChange(data) },
      { condition: this.isReAssignBooking(data), action: () => this.getReassignBookingAction(data) }
    ];
  }

  getQuickCommerceBookingActions(data: any): any[] {
    return [
      { condition: this.shouldShowQcBookingStatusChange(data), action: () => this.getQuickCommerceBookingAction(data) },
      // { condition: this.shouldShowPickerActionForQc(data), action: () => this.getBookingDeliveryActionChange(data) },
      { condition: this.shouldShowPickerActionForQc(data), action: () => this.getAcceptRejectActionChange(data, UserType.PICKER) },
      { condition: this.shouldAssignDeliveryManAction(data), action: () => this.getAssignDeliverymanAction(data) },
      { condition: this.shouldShowDriverStatusChange(data), action: () => this.getBookingDeliveryActionChange(data) }
    ];
  }

  getDeliveryBookingActions(data: any): any[] {
    return [
      { condition: this.shouldAssignMerchantAction(data), action: () => this.getAssignMerchantAction(data) },
      { condition: this.shouldAssignDeliveryManAction(data), action: () => this.getAssignDeliverymanAction(data) },
      { condition: this.shouldShowDriverStatusChange(data), action: () => this.getBookingDeliveryActionChange(data) }
    ];
  }

  getServiceBookingActions(data: any): any[] {
    return [
      { condition: this.shouldShowActionForService(data), action: () => this.getActionsForService(data), isArray: true },
      { condition: this.shouldShowMerchantActionForService(data), action: () => this.getMerchantActionForService(data) },
      { condition: this.shouldShowDriverStatusChange(data), action: () => this.getBookingDeliveryActionChange(data) }
    ];
  }

  private shouldShowAcceptRejectAction(data: any): boolean {
    return this.helper.hasPermission(this.helper.Permissions.EDIT) && data.status === this.helper.BookingStatus.ASSIGNED;
  }

  private getAcceptRejectActionChange(data: any, userType?: UserType): any {
    return {
      label: this.getUnifiedStatusButtonLabel(data?.status, data.quickCommerce?.status, data.businessType, data.bookingType, data.bookingTags),
      icon: "ri-shield-check-line align-bottom me-2 text-muted float-start",
      onClick: (): void => {
        if (userType === UserType.PICKER) {
          return this.openPickerListModel(data);
        }
        return this.openDriverListModel(data);
      }
    };
  }

  /**
   * Opens the driver list modal.
   * @param data The booking data.
   */
  openDriverListModel(data: any): void {
    this.driverListModel.openModal(data);
  }

  // ─────────────────────────────────────────────────────────────────────────────────────────
  // ▶                TAXI STATUS FOR BOOKING SECTION START
  // ─────────────────────────────────────────────────────────────────────────────────────────

  private getBookingDeliveryActionChange(data: any): any {
    return {
      label: this.getUnifiedStatusButtonLabel(data?.status, data.quickCommerce?.status, data.businessType, data.bookingType, data.bookingTags),
      icon: "ri-taxi-fill",
      onClick: () => this.openBookingStatusChangeConfirmationModal(data)
    };
  }

  // ─────────────────────────────────────────────────────────────────────────────────────────
  // ▶                QUICK COMMERCE STATUS FOR BOOKING SECTION START
  // ─────────────────────────────────────────────────────────────────────────────────────────

  private shouldShowQcBookingStatusChange(data: any): boolean {
    return (
      this.isBusinessTypeQuickCommerce() &&
      data?.quickCommerce?.merchantDetail?.id &&
      this.isShowQcStatusChangeBtn(data.quickCommerce?.status) &&
      this.bookingHistoryMainType === this.helper.BookingHistoryMainType.RUNNING
    );
  }

  private getQuickCommerceBookingAction(data: any): any {
    return {
      label: this.getUnifiedStatusButtonLabel(data.quickCommerce?.status, data.quickCommerce?.status, this.helper.BusinessType.QUICK_COMMERCE, data.bookingType, data.bookingTags),
      icon: "ri-store-2-fill",
      onClick: () => this.openBookingStatusChangeConfirmationModalForQuickCommerce(data)
    };
  }

  private shouldShowPickerActionForQc(data: any): boolean {
    return this.helper.hasPermission(this.helper.Permissions.EDIT) && data?.quickCommerce?.status === this.helper.BookingStatus.PICKER_ASSIGNED;
  }

  // ─────────────────────────────────────────────────────────────────────────────────────────
  // ▶                DELIVERY STATUS FOR BOOKING SECTION START
  // ─────────────────────────────────────────────────────────────────────────────────────────

  isShowDeliveryStatusChangeBtn(data: any): boolean {
    let isShow =
      data?.quickCommerce?.status === this.helper.BookingStatus.REQUESTED ||
      data?.quickCommerce?.status === this.helper.BookingStatus.MERCHANT_ACCEPTED ||
      data?.quickCommerce?.status === this.helper.BookingStatus.MERCHANT_PREPARING ||
      data?.quickCommerce?.status === this.helper.BookingStatus.PICKED ||
      (data?.quickCommerce?.status === this.helper.BookingStatus.MERCHANT_COMPLETED && this.isBookingTypePickup(data));

    // handle preparing button only show before 30 minutes
    if (this.bookingHistoryMainType === this.helper.BookingHistoryMainType.SCHEDULED && data?.quickCommerce?.status === this.helper.BookingStatus.MERCHANT_ACCEPTED) {
      const timeDifference = this.helper.dateDiffInMinutes(new Date(data.bookingTime), new Date());
      if (timeDifference > 30) {
        isShow = false;
      }
    }
    return isShow;
  }

  private shouldAssignDeliveryManAction(data: any): boolean {
    return (
      (data.status === this.helper.BookingStatus.REQUESTED || data.status === this.helper.BookingStatus.NO_DRIVER_FOUND) &&
      this.bookingHistoryMainType === this.helper.BookingHistoryMainType.RUNNING &&
      this.checkDeliverymanAssignCondtionAsBusiness(data)
    );
  }

  private getAssignDeliverymanAction(data: any): any {
    return {
      label: this.helper.TitleStatusString[this.helper.TitleStatus.ASSIGN_DELIVERYMAN],
      icon: "ri-store-2-fill",
      onClick: () => this.openDeliveryBookingStatusChangeConfirmationModal(data)
    };
  }

  checkDeliverymanAssignCondtionAsBusiness(data: any): boolean {
    switch (data.businessType) {
      case this.helper.BusinessType.TAXI:
        return true; // Taxi always returns true
      case this.helper.BusinessType.QUICK_COMMERCE:
        return data.quickCommerce?.status === this.helper.BookingStatus.PICKER_ACCEPTED && data.quickCommerce.merchantDetail?.id;
      case this.helper.BusinessType.DELIVERY:
        return data.quickCommerce?.status === this.helper.BookingStatus.MERCHANT_COMPLETED && data.quickCommerce.merchantDetail?.id && !this.isBookingTypePickup(data);
      case this.helper.BusinessType.SERVICE:
        return data.quickCommerce.merchantDetail?.id && data.quickCommerce?.status === this.helper.BookingStatus.MERCHANT_ACCEPTED;
      case this.helper.BusinessType.TAXI_METER:
        return true;
      case this.helper.BusinessType.TOWING:
        return true;
        default:
        return false; // If none of the cases match, return false
    }
  }

  private shouldAssignMerchantAction(data: any): boolean {
    return (
      this.isShowDeliveryStatusChangeBtn(data) &&
      (this.bookingHistoryMainType === this.helper.BookingHistoryMainType.RUNNING || this.bookingHistoryMainType === this.helper.BookingHistoryMainType.SCHEDULED) &&
      data.quickCommerce.merchantDetail?.id &&
      this.isBusinessTypeDelivery()
    );
  }

  private getAssignMerchantAction(data: any): any {
    return {
      label: this.setDeliveryStatusButtonString(data),
      icon: "ri-store-2-fill",
      onClick: () => this.openDeliveryBookingStatusChangeConfirmationModal(data)
    };
  }

  setDeliveryStatusButtonString(data: any): string {
    if (data.quickCommerce?.status === this.helper.BookingStatus.REQUESTED) {
      return this.helper.TitleStatusString[this.helper.TitleStatus.MERCHANT_ACCEPT];
    } else if (data.quickCommerce?.status === this.helper.BookingStatus.MERCHANT_ACCEPTED) {
      return this.helper.TitleStatusString[this.helper.TitleStatus.START_PREPARING];
    } else if (data.quickCommerce?.status === this.helper.BookingStatus.MERCHANT_PREPARING) {
      return this.helper.TitleStatusString[this.helper.TitleStatus.ORDER_READY];
    } else if (data.quickCommerce?.status === this.helper.BookingStatus.PICKED) {
      return this.helper.BookingStatusStrings[this.helper.BookingStatus.STARTED];
    } else if (data?.status === this.helper.BookingStatus.STARTED || data?.status === this.helper.BookingStatus.ARRIVED_NEAR_DESTINATION) {
      return this.helper.TitleStatusString[this.helper.TitleStatus.START];
    } else if (data?.quickCommerce?.status === this.helper.BookingStatus.MERCHANT_COMPLETED && this.isBookingTypePickup(data)) {
      return this.helper.TitleStatusString[this.helper.TitleStatus.COMPLETE];
    }
    return "";
  }

  // ─────────────────────────────────────────────────────────────────────────────────────────
  // ▶                SERVICE STATUS FOR BOOKING SECTION START
  // ─────────────────────────────────────────────────────────────────────────────────────────

  private shouldShowActionForService(data: any): boolean {
    return (
      this.helper.hasPermission(this.helper.Permissions.EDIT) &&
      (data?.status === this.helper.BookingStatus.REQUESTED || data?.status === this.helper.BookingStatus.NO_DRIVER_FOUND) &&
      this.bookingHistoryMainType === this.helper.BookingHistoryMainType.RUNNING &&
      this.isBusinessTypeService()
    );
  }

  private getActionsForService(data: any): any[] {
    const actions = [
      {
        label: this.helper.TitleStatusString[this.helper.TitleStatus.ASSIGN_DELIVERYMAN],
        icon: "ri-store-2-fill",
        onClick: (): void => this.openServiceAssignDriverMerchantModal(data, this.helper.UserType.DRIVER)
      }
    ];
    const isMerchantAssign = data.quickCommerce?.merchantDetail?.id;

    // Always add merchant assignment action if no merchant is assigned
    if (!isMerchantAssign) {
      actions.push({
        label: this.helper.TitleStatusString[this.helper.TitleStatus.ASSIGN_MERCHANT],
        icon: "ri-store-2-fill",
        onClick: () => this.openServiceAssignDriverMerchantModal(data, this.helper.UserType.MERCHANT)
      });
    }
    return actions;
  }

  private shouldShowMerchantActionForService(data: any): boolean {
    return (
      data.quickCommerce?.merchantDetail?.id &&
      data?.status === this.helper.BookingStatus.REQUESTED &&
      this.bookingHistoryMainType === this.helper.BookingHistoryMainType.RUNNING &&
      this.isBusinessTypeService()
    );
  }

  private getMerchantActionForService(data: any): any {
    return {
      label: this.helper.TitleStatusString[this.helper.TitleStatus.MERCHANT_ACCEPT],
      icon: "ri-store-2-fill",
      onClick: () => this.openDeliveryBookingStatusChangeConfirmationModal(data)
    };
  }

  // ─────────────────────────────────────────────────────────────────────────────────────────
  // ▶                REASSIGN STATUS FOR BOOKING SECTION START
  // ─────────────────────────────────────────────────────────────────────────────────────────

  private isReAssignBooking(data: any): boolean {
    return (
      (this.isBusinessTypeTaxi() || this.isBusinessTypeTaxiMeter() || this.isBusinessTypeTowing()) &&
      (this.bookingHistoryMainType === this.helper.BookingHistoryMainType.SCHEDULED || this.bookingHistoryMainType === this.helper.BookingHistoryMainType.RUNNING) &&
      (data?.status === this.helper.BookingStatus.NO_DRIVER_FOUND || data?.status === this.helper.BookingStatus.REQUESTED)
    );
  }

  private getReassignBookingAction(data: any): any {
    return {
      label: this.helper.TitleStatusString[this.helper.TitleStatus.REASSIGN],
      icon: "ri-edit-box-fill",
      onClick: () => this.onReassignBooking(data, this.bookingHistoryMainType)
    };
  }

  // ─────────────────────────────────────────────────────────────────────────────────────────
  // ▶               CANCEL FOR BOOKING SECTION START
  // ─────────────────────────────────────────────────────────────────────────────────────────

  private shouldShowCancelAction(data: any): boolean {
    return data?.status < this.helper.BookingStatus.ARRIVED_AT_DESTINATION;
  }

  private getCancelAction(data: any): any {
    return {
      label: this.helper.TitleStatusString[this.helper.TitleStatus.CANCEL],
      icon: "ri-close-fill",
      onClick: () => this.openBookingStatusChangeConfirmationModal(data, true)
    };
  }

  // ─────────────────────────────────────────────────────────────────────────────────────────
  // ▶               PAUSE / RESUME FOR BOOKING SECTION START
  // ─────────────────────────────────────────────────────────────────────────────────────────

  /**
   * Pausing is limited to the pre-acceptance states the server accepts, but an already-paused
   * booking always offers Resume whatever its status: a driver can accept seconds after a pause
   * (offers are not recalled) and the hold survives that accept, so otherwise the operator would
   * see a Paused badge with no way to clear it.
   */
  private shouldShowPauseAction(data: any): boolean {
    if (data?.isPaused) return true;
    return [this.helper.BookingStatus.REQUESTED, this.helper.BookingStatus.ASSIGNED, this.helper.BookingStatus.NO_DRIVER_FOUND, this.helper.BookingStatus.NOT_ANSWERED].includes(data?.status);
  }

  private getPauseAction(data: any): any {
    return {
      label: data.isPaused ? "label-title.resume-booking" : "label-title.pause-booking",
      icon: data.isPaused ? "ri-play-circle-line" : "ri-pause-circle-line",
      onClick: () => this.toggleBookingPause(data)
    };
  }

  toggleBookingPause(data: any): void {
    this.historyService.setBookingPauseState(data._id, !data.isPaused).subscribe((success: boolean) => {
      if (success) {
        this.getBookingHistoryList(this.filterData, false);
      }
    });
  }

  // ─────────────────────────────────────────────────────────────────────────────────────────
  // ▶               GET STRING START
  // ─────────────────────────────────────────────────────────────────────────────────────────

  getUnifiedStatusButtonLabel(deliveryStatus: number, orderStatus: number, businessType: number, bookingType: number, bookingTags: any[]): string {
    if (deliveryStatus === this.helper.BookingStatus.ASSIGNED) {
      return this.helper.TitleStatusString[this.helper.TitleStatus.ACCEPT_REJECT];
    }
    if (businessType === this.helper.BusinessType.QUICK_COMMERCE) {
      return this.getQuickCommerceLabel(deliveryStatus, orderStatus);
    }
    const isPickupFromHome = bookingTags.includes(this.helper.BookingType[this.helper.BookingType.PICKUP_FROM_HOME]);
    const isDropAtHome = bookingTags.includes(this.helper.BookingType[this.helper.BookingType.DROP_AT_HOME]);

    if (deliveryStatus === this.helper.BookingStatus.ACCEPTED) {
      return this.getAcceptedLabel(bookingType, isPickupFromHome);
    }

    if (deliveryStatus === this.helper.BookingStatus.SERVICE_COMPLETED) {
      return isDropAtHome ? this.helper.TitleStatusString[this.helper.TitleStatus.COMING] : this.helper.BookingStatusStrings[this.helper.AllBookingStatus.ARRIVED_AT_DESTINATION];
    }

    if (deliveryStatus === this.helper.BookingStatus.IN_ROUTE) {
      return this.helper.TitleStatusString[this.helper.TitleStatus.ARRIVED];
    }

    if (deliveryStatus === this.helper.BookingStatus.ARRIVED_AT_PICKUP) {
      return this.getArrivedAtPickupLabel(orderStatus, bookingType, isPickupFromHome);
    }

    if (deliveryStatus === this.helper.BookingStatus.PICKED) {
      return this.helper.TitleStatusString[this.helper.TitleStatus.START];
    }

    if (deliveryStatus === this.helper.BookingStatus.STARTED || deliveryStatus === this.helper.BookingStatus.ARRIVED_AT_STOP) {
      return this.getStartedOrArrivedStopLabel(businessType, bookingType, isDropAtHome);
    }

    return "";
  }

  private getAcceptedLabel(bookingType: number, isPickupFromHome: boolean): string {
    if (bookingType === this.helper.BookingType.ONLINE_SERVICE || (bookingType === this.helper.BookingType.ON_SITE_SERVICE && !isPickupFromHome)) {
      return this.helper.TitleStatusString[this.helper.TitleStatus.START];
    }
    return this.helper.TitleStatusString[this.helper.TitleStatus.COMING];
  }

  private getArrivedAtPickupLabel(orderStatus: number, bookingType: number, isPickupFromHome: boolean): string {
    if (orderStatus === BookingStatus.SERVICE_COMPLETED) {
      return this.helper.BookingStatusStrings[BookingStatus.SERVICE_COMPLETED];
    }
    if (bookingType === this.helper.BookingType.ON_SITE_SERVICE && isPickupFromHome) {
      return this.helper.BookingStatusStrings[BookingStatus.PICKED];
    }
    return this.helper.TitleStatusString[this.helper.TitleStatus.START];
  }

  private getStartedOrArrivedStopLabel(businessType: number, bookingType: number, isDropAtHome: boolean): string {
    if (bookingType === this.helper.BookingType.ON_SITE_SERVICE && isDropAtHome) {
      return this.helper.BookingStatusStrings[BookingStatus.SERVICE_COMPLETED];
    }
    return businessType === this.helper.BusinessType.DELIVERY || businessType === this.helper.BusinessType.QUICK_COMMERCE || businessType === this.helper.BusinessType.TAXI || businessType === this.helper.BusinessType.TAXI_METER || businessType === this.helper.BusinessType.TOWING
      ? this.helper.BookingStatusStrings[BookingStatus.ARRIVED_AT_DESTINATION]
      : this.helper.BookingStatusStrings[BookingStatus.SERVICE_COMPLETED];
  }

  private getQuickCommerceLabel(deliveryStatus: number, orderStatus: number): string {
    if (orderStatus === BookingStatus.REQUESTED) {
      return this.helper.BookingStatusStrings[BookingStatus.MERCHANT_ACCEPTED];
    } else if (orderStatus === BookingStatus.MERCHANT_ACCEPTED || orderStatus === BookingStatus.NO_PICKER_FOUND) {
      return this.helper.TitleStatusString[this.helper.TitleStatus.ASSIGN_PICKER];
    } else if (orderStatus === BookingStatus.PICKER_ASSIGNED) {
      return this.helper.TitleStatusString[this.helper.TitleStatus.PICKER_ACCEPT_REJECT];
    } else if (deliveryStatus === BookingStatus.ARRIVED_AT_PICKUP) {
      return this.helper.BookingStatusStrings[BookingStatus.PICKED];
    } else if (deliveryStatus === BookingStatus.PICKED) {
      return this.helper.TitleStatusString[this.helper.TitleStatus.START];
    } else if (deliveryStatus === BookingStatus.STARTED) {
      return this.helper.BookingStatusStrings[BookingStatus.ARRIVED_NEAR_DESTINATION];
    } else if (deliveryStatus === BookingStatus.ARRIVED_NEAR_DESTINATION) {
      return this.helper.TitleStatusString[this.helper.TitleStatus.COMPLETE];
    }
    return this.helper.translateService.instant("label-title.unknown-status");
  }

  private shouldShowDriverStatusChange(data: any): boolean {
    return (
      data.confirmedDriver &&
      data?.status >= this.helper.BookingStatus.ACCEPTED &&
      data?.status < this.helper.BookingStatus.ARRIVED_AT_DESTINATION &&
      this.bookingHistoryMainType === this.helper.BookingHistoryMainType.RUNNING
    );
  }

  /**
   * Opens the picker list modal.
   * @param data The booking data.
   */
  openPickerListModel(data: any): void {
    this.driverListModel.openModal(data, UserType.PICKER);
  }
}
